import { useState } from "react";
import { PageShell, DemoCTA } from "../components/PageShell";
import AltitudesSection from "../components/AltitudesSection";
import CostSection from "../components/CostSection";
import { CayusWave } from "../components/CayusWave";
import { TrendingDown, Zap, BarChart3, Target } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { usePages } from "../translations/pages";

const ICONS = [<TrendingDown className="w-5 h-5" />, <Zap className="w-5 h-5" />, <BarChart3 className="w-5 h-5" />, <Target className="w-5 h-5" />];

const fmt = (n, lang) => new Intl.NumberFormat(lang === "en" ? "en-GB" : lang === "es" ? "es-ES" : "fr-FR").format(Math.round(n));

const Field = ({ label, value, onChange, suffix, step = 1, testid }) => (
  <label className="block">
    <span className="mono-cayus text-[10px] text-[#5E6C83] block mb-1.5">{label}</span>
    <div className="flex items-center gap-2 bg-white border border-[#C3CFE0] rounded-xl px-3 py-2">
      <input
        type="number"
        value={value}
        step={step}
        min={0}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full font-body-cayus text-[#0A2342] font-semibold focus:outline-none bg-transparent"
        data-testid={testid}
      />
      {suffix && <span className="text-sm text-[#9098A3] flex-shrink-0">{suffix}</span>}
    </div>
  </label>
);

export default function PourquoiCayusPage() {
  const { language } = useLanguage();
  const p = usePages(language).pourquoi;
  const [effectif, setEffectif] = useState(500);
  const [turnover, setTurnover] = useState(12);
  const [regrette, setRegrette] = useState(40);
  const [cout, setCout] = useState(45000);

  const departs = (effectif * turnover) / 100;
  const regrettes = (departs * regrette) / 100;
  const coutTotal = regrettes * cout;

  return (
    <PageShell testid="pourquoi-page">
      {/* Hero */}
      <div className="wrap-cayus mb-14">
        <p className="mono-cayus text-[#0047AB] mb-4">{p.eyebrow}</p>
        <h1 className="font-extrabold text-4xl lg:text-5xl text-[#1B1B1B] tracking-[-0.03em] mb-5">
          {p.title}
        </h1>
      </div>
      <CayusWave className="w-full h-8 mb-14" opacity={0.3} />

      {/* 4 impacts */}
      <section className="wrap-cayus mb-20" data-testid="impacts-section">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {p.impacts.map((im, i) => (
            <div key={i} className="border border-[#C3CFE0] rounded-xl p-6">
              <div className="w-10 h-10 rounded-xl bg-[#0047AB]/10 text-[#0047AB] flex items-center justify-center mb-4">{ICONS[i]}</div>
              <h3 className="font-display font-semibold text-lg text-[#0A2342] mb-2">{im.t}</h3>
              <p className="font-body-cayus text-sm text-[#4D4D4D] leading-relaxed">{im.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pourquoi les outils actuels échouent */}
      <section className="bg-[#F7F9FC] py-16 mb-20" data-testid="tools-fail-section">
        <div className="wrap-cayus">
          <h2 className="font-bold text-3xl text-[#1B1B1B] tracking-[-0.025em] mb-10">
            {p.failTitle}
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white border border-[#C3CFE0] rounded-xl p-7">
              <p className="mono-cayus text-[10px] text-[#9098A3] mb-2">{p.surveys}</p>
              <p className="font-body-cayus text-sm text-[#4D4D4D] leading-relaxed">{p.surveysText}</p>
            </div>
            <div className="bg-white border border-[#C3CFE0] rounded-xl p-7">
              <p className="mono-cayus text-[10px] text-[#9098A3] mb-2">{p.analytics}</p>
              <p className="font-body-cayus text-sm text-[#4D4D4D] leading-relaxed">{p.analyticsText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Calculateur */}
      <section className="wrap-cayus mb-20" data-testid="attrition-calculator">
        <p className="mono-cayus text-[#0047AB] mb-4">{p.calcEyebrow}</p>
        <h2 className="font-bold text-3xl text-[#1B1B1B] tracking-[-0.025em] mb-3">
          {p.calcTitle}
        </h2>
        <p className="font-body-cayus text-sm text-[#5E6C83] mb-10 max-w-2xl">
          {p.calcLead}
        </p>
        <div className="border border-[#C3CFE0] rounded-xl overflow-hidden">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6 bg-[#F7F9FC]">
            <Field label={p.fEffectif} value={effectif} onChange={setEffectif} testid="calc-effectif" />
            <Field label={p.fTurnover} value={turnover} onChange={setTurnover} suffix="%" testid="calc-turnover" />
            <Field label={p.fRegrette} value={regrette} onChange={setRegrette} suffix="%" testid="calc-regrette" />
            <Field label={p.fCout} value={cout} onChange={setCout} suffix="€" step={1000} testid="calc-cout" />
          </div>
          <div className="ink-section p-8 text-center">
            <p className="mono-cayus text-[10px] text-[#9ECEFA] mb-4">
              {fmt(departs, language)} {p.resultLine} {fmt(regrettes, language)} {p.resultRegret}
            </p>
            <p className="mono-cayus text-3xl sm:text-4xl text-white font-semibold" data-testid="calculator-result">
              {fmt(coutTotal, language)} {p.perYear}
            </p>
            <p className="font-body-cayus text-sm text-[#9098A3] mt-3">
              {p.hypo}
            </p>
          </div>
        </div>
      </section>

      {/* Les quatre vues (déplacées de la page d'accueil) */}
      <div className="mb-4">
        <AltitudesSection />
      </div>

      {/* Le détail du coût de l'angle mort (déplacé de la page d'accueil) */}
      <div className="mb-16">
        <CostSection />
      </div>

      {/* Clôture */}
      <section className="wrap-cayus text-center mb-20">
        <p className="pull-quote text-xl sm:text-2xl text-[#0A2342] leading-relaxed">
          « {p.closing} »
        </p>
      </section>

      <DemoCTA />
    </PageShell>
  );
}
